import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Popconfirm, Segmented, Space, Tag, Typography } from 'antd';
import type { TableColumnType } from 'antd';
import { Archive, Component as ComponentIcon, Pencil, Plus, Send, Trash2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { DataTable } from '@shared/components/DataTable';
import { useCan } from '@shared/rbac/useCan';
import { PERMISSIONS } from '@shared/rbac/permissions.const';
import { ROUTES } from '@shared/constants/routes';
import { componentsApi } from '@api/components';
import { NormalizedApiError } from '@api/errors';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import type { ComponentListItem, ComponentStatus } from '../types';
import { ComponentCreateModal, type ComponentCreateResult } from '../components/ComponentCreateModal';
import { buildCreateSeed } from '../blocks/templates';

const { Text } = Typography;
type StatusFilter = 'ALL' | ComponentStatus;
const STATUSES: ComponentStatus[] = ['Draft', 'Published', 'Archived'];

const STATUS_COLOR: Record<ComponentStatus, string> = {
  Draft: 'default',
  Published: 'green',
  Archived: 'orange',
};

interface InUseState {
  row: ComponentListItem;
  count: number;
}

export function ComponentsPage() {
  const { t } = useTranslation(['component-studio', 'common']);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canManage = useCan(PERMISSIONS.COMPONENT_MANAGE);

  const [status, setStatus] = useState<StatusFilter>('ALL');
  const [modalOpen, setModalOpen] = useState(false);
  const [inUse, setInUse] = useState<InUseState | null>(null);

  const { data: components = [], isLoading } = useQuery({
    queryKey: ['components', { status }],
    queryFn: () => componentsApi.list({ status: status === 'ALL' ? undefined : status }),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['components'] });
  const invalidatePalette = () => qc.invalidateQueries({ queryKey: ['component-palette'] });

  const createMutation = useMutation({
    // K-37 — create HER ZAMAN kaynak taşır (BE: COMPONENT_SOURCE_REQUIRED): şablon
    // seçiliyse şablon ağacı, Boş modda boş-geçerli ağaç. Tek istek, tek versiyon.
    mutationFn: (input: ComponentCreateResult) =>
      componentsApi.create({
        name: input.name,
        category: input.category,
        componentJson: buildCreateSeed(input.templateId),
      }),
    onSuccess: async (created) => {
      toast.success(t('toast.created'));
      setModalOpen(false);
      await invalidate();
      void navigate(ROUTES.COMPONENT_EDIT.replace(':id', created.id));
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const publishMutation = useMutation({
    mutationFn: (row: ComponentListItem) => componentsApi.publish(row.id),
    onSuccess: async () => {
      toast.success(t('toast.published'));
      await Promise.all([invalidate(), invalidatePalette()]);
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const archiveMutation = useMutation({
    mutationFn: (row: ComponentListItem) => componentsApi.archive(row.id),
    onSuccess: async () => {
      toast.success(t('toast.archived'));
      setInUse(null);
      await Promise.all([invalidate(), invalidatePalette()]);
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (row: ComponentListItem) => componentsApi.remove(row.id),
    onSuccess: async () => {
      toast.success(t('toast.deleted'));
      await Promise.all([invalidate(), invalidatePalette()]);
    },
    onError: (err, row) => {
      // 409 COMPONENT_IN_USE → "N sayfada kullanılıyor" + Arşivle önerisi.
      if (err instanceof NormalizedApiError && err.code === 'COMPONENT_IN_USE') {
        const count = Number(err.data?.usageCount ?? 0) || 0;
        setInUse({ row, count });
        return;
      }
      toast.error(resolveApiErrorMessage(err));
    },
  });

  const goEdit = (id: string) => void navigate(ROUTES.COMPONENT_EDIT.replace(':id', id));

  const columns: TableColumnType<ComponentListItem>[] = [
    {
      title: t('list.columns.name'),
      dataIndex: 'name',
      key: 'name',
      render: (name: string, row) => (
        <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => goEdit(row.id)}>
          {name || t('editor.notFound')}
        </Button>
      ),
    },
    {
      title: t('list.columns.category'),
      dataIndex: 'category',
      key: 'category',
      width: 160,
      render: (c: string) => (c ? <Tag>{c}</Tag> : <Text type="secondary">—</Text>),
    },
    {
      title: t('list.columns.status'),
      key: 'status',
      width: 120,
      render: (_, r) => <Tag color={STATUS_COLOR[r.status]}>{t(`status.${r.status}`)}</Tag>,
    },
    {
      title: t('list.columns.updatedAt'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 150,
      render: (v: string) => (v ? dayjs(v).format('DD.MM.YYYY HH:mm') : '—'),
    },
    {
      title: t('common:actions.title'),
      key: 'actions',
      align: 'right',
      width: 160,
      render: (_, r) => (
        <Space size={4}>
          {canManage && r.status !== 'Published' ? (
            <Button
              type="text"
              size="small"
              icon={<Send size={15} />}
              onClick={() => publishMutation.mutate(r)}
              loading={publishMutation.isPending}
              aria-label={t('toast.published')}
              title={t('status.Published')}
            />
          ) : null}
          {canManage && r.status !== 'Archived' ? (
            <Button
              type="text"
              size="small"
              icon={<Archive size={15} />}
              onClick={() => archiveMutation.mutate(r)}
              loading={archiveMutation.isPending}
              aria-label={t('delete.archiveInstead')}
              title={t('status.Archived')}
            />
          ) : null}
          <Button
            type="text"
            size="small"
            icon={<Pencil size={15} />}
            onClick={() => goEdit(r.id)}
            aria-label={t('list.editComponent')}
            title={t('list.editComponent')}
          />
          <Popconfirm
            title={t('delete.title')}
            description={t('delete.confirm', { name: r.name })}
            okText={t('common:actions.delete')}
            okButtonProps={{ danger: true }}
            cancelText={t('common:actions.cancel')}
            onConfirm={() => deleteMutation.mutate(r)}
            disabled={!canManage}
          >
            <Button
              type="text"
              size="small"
              danger
              icon={<Trash2 size={15} />}
              disabled={!canManage}
              aria-label={t('common:actions.delete')}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const statusOptions = useMemo(
    () => [
      { value: 'ALL' as const, label: t('filter.all') },
      ...STATUSES.map((s) => ({ value: s, label: t(`status.${s}`) })),
    ],
    [t],
  );

  return (
    <div data-testid="components-page">
      <PageHeader
        title={t('title')}
        description={t('description')}
        breadcrumb={[{ label: t('common:labels.home'), to: '/' }, { label: t('title') }]}
        actions={
          canManage ? (
            <Button
              type="primary"
              icon={<Plus size={16} />}
              onClick={() => setModalOpen(true)}
              data-testid="component-new"
            >
              {t('new')}
            </Button>
          ) : null
        }
      />

      <DataTable<ComponentListItem>
        data={components}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        emptyState={
          <div style={{ textAlign: 'center', padding: 32 }}>
            <ComponentIcon size={28} style={{ color: 'var(--ds-text-tertiary)' }} />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">{t('empty')}</Text>
            </div>
          </div>
        }
        filters={
          <Segmented<StatusFilter> size="small" value={status} onChange={setStatus} options={statusOptions} />
        }
      />

      <ComponentCreateModal
        open={modalOpen}
        saving={createMutation.isPending}
        onCancel={() => setModalOpen(false)}
        onCreate={(input) => createMutation.mutate(input)}
      />

      <Modal
        open={inUse !== null}
        title={t('delete.inUseTitle')}
        onCancel={() => setInUse(null)}
        okText={t('delete.archiveInstead')}
        cancelText={t('common:actions.cancel')}
        confirmLoading={archiveMutation.isPending}
        onOk={() => inUse && archiveMutation.mutate(inUse.row)}
        data-testid="component-inuse-modal"
      >
        <Text>{inUse ? t('delete.inUse', { count: inUse.count }) : ''}</Text>
      </Modal>
    </div>
  );
}
